import type { Article } from "./types";

export const article: Article = {
  slug: "screen-time-limits-dont-work",
  title: "Why Screen Time limits stop working after a week",
  h1: "Why your Screen Time limits stopped working",
  description:
    "Screen Time limits fail for one structural reason: the button that removes them costs nothing. Here is what that costs you, and what actually holds.",
  targets: [
    "why screen time limits don't work",
    "how to make screen time limits stick",
    "ask for more time keeps happening",
    "screen time limit not working iphone",
  ],
  published: "2026-08-17",
  standfirst:
    "You set them on a Sunday, meaning it. By Thursday you are tapping through them without reading. That is not weakness — it is the design.",
  takeaways: [
    "Screen Time limits fail because the button that removes them costs nothing — “Ignore Limit” is one tap, and you are the only person on the other end of the request.",
    "A Screen Time passcode helps until you learn it, or until the person holding it gets tired of being asked.",
    "Blocks that survive share three properties: they cannot be dismissed in one tap, they end in an action rather than a wait, and they apply every day without negotiation.",
    "Set it up tonight as: two apps, one window, an unlock cost you would accept at 11pm, and no adjustments for two weeks.",
  ],
  body: [
    {
      type: "p",
      text: "Almost everyone who installs an app blocker has already tried Screen Time. They set a two-hour limit on Instagram, felt organised about it, and within a week were tapping “Ignore Limit” with the same thumb motion they use to unlock the phone. The usual explanation is that they lack discipline. The real explanation is simpler and much less personal.",
    },
    {
      type: "h2",
      id: "the-button",
      text: "The limit is only as strong as the button that removes it",
    },
    {
      type: "p",
      text: "When an iPhone limit runs out, iOS shows a screen with a single obvious action: ask for fifteen more minutes, or ignore the limit for the rest of the day. If you set the limit on your own phone, nobody is on the other end of that request. You are asking yourself, and you are asking at the exact moment you least want to say no.",
    },
    {
      type: "p",
      text: "So the whole system rests on a decision made in the worst possible conditions — tired, mid-scroll, three seconds of friction between you and the thing you were already doing. A rule you can revoke in one tap is not a rule. It is a reminder, and reminders stop being visible after about four days.",
    },
    {
      type: "note",
      text: "The failure is not that the limit was too strict. It is that removing it was free.",
    },
    {
      type: "h2",
      id: "passcode",
      text: "The Screen Time passcode helps, and then it does not",
    },
    {
      type: "p",
      text: "The standard advice is to set a Screen Time passcode you cannot remember, or to have someone else set it. This does work, for a while, and it is worth doing if you have someone who will hold it for you. But it fails in two predictable ways.",
    },
    {
      type: "ol",
      items: [
        "You learn the passcode anyway. You typed it enough times while setting things up, or you wrote it down “temporarily”. Once you know it, you are back to a one-tap rule with extra steps.",
        "The person holding it gets tired of being asked. Nobody wants to be their spouse's screen-time parole officer, and after the third late-night text asking for the code, the arrangement quietly ends.",
      ],
    },
    {
      type: "p",
      text: "There is also a third, quieter failure: a passcode makes the block feel like a punishment being administered to you. That framing is corrosive. You start looking for the loophole rather than for the reason you wanted the limit in the first place.",
    },
    {
      type: "h2",
      id: "friction",
      text: "What actually holds: friction that goes somewhere",
    },
    {
      type: "p",
      text: "The blocks that survive past week two have one thing in common. They do not merely stand in the way — they hand you something to do instead. The moment of friction has a destination, so unlocking is not a defeat you talk yourself into but a small act you complete.",
    },
    {
      type: "p",
      text: "This is the difference between a locked door and a turnstile. A locked door invites you to look for the key. A turnstile asks for one small thing and then lets you through, and because it always lets you through, you stop resenting it — which is precisely why you keep it.",
    },
    {
      type: "p",
      text: "In practice, three properties separate a block that lasts from one that does not:",
    },
    {
      type: "ul",
      items: [
        "It cannot be dismissed in one tap. Removing it should take an amount of effort that matches how much you meant it when you set it up.",
        "It ends in an action, not in waiting. A sixty-second countdown teaches you to stare at a timer; a thing to actually do teaches you something else.",
        "It is the same every time. A rule that applies on some evenings and not others is a negotiation you re-open nightly, and you will eventually win.",
      ],
    },
    {
      type: "h2",
      id: "prayer",
      text: "Why a prayer works where a countdown does not",
    },
    {
      type: "p",
      text: "{Bible-Shield} is built on that third option. The apps you choose close at the times you choose, and they open again after a short prayer written for the day you are actually having. It uses Apple's own Screen Time framework underneath — the same mechanism as the limits above — but the exit is different, and the exit is the whole design.",
      links: { "Bible-Shield": "/" },
    },
    {
      type: "p",
      text: "You are not asked to sit and wait, and you are not asked to prove anything. You are asked for a minute of attention, pointed somewhere other than the feed. Then the apps come back. Nothing is confiscated and nothing is withheld.",
    },
    {
      type: "verse",
      verse: {
        text: "Be still, and know that I am God.",
        ref: "Psalm 46:10",
      },
    },
    {
      type: "p",
      text: "The point was never to use the phone less for the sake of using it less. It is what happens in the minute you just got back — and a minute that ends in stillness is a different minute from one that ends in a countdown reaching zero.",
    },
    {
      type: "h2",
      id: "tonight",
      text: "If you want to fix it tonight",
    },
    {
      type: "p",
      text: "Whatever tool you use, set it up in this order — and if you are still deciding which, the six questions in {how-to-choose-a-christian-app-blocker} are the ones that matter. It takes about five minutes and it survives the week better than a limit set in a burst of resolve.",
      links: {
        "how-to-choose-a-christian-app-blocker":
          "/blog/christian-app-blocker-guide",
      },
    },
    {
      type: "ol",
      items: [
        "Pick the two apps that actually take your evenings. Not the ten you feel vaguely guilty about — the two you open without deciding to.",
        "Pick one window, not a daily allowance. “After 9pm” is a rule you can keep; “two hours a day” is an accounting exercise you will lose track of by Wednesday.",
        "Decide now what the unlock costs, and make it something you would be willing to do at 11pm. If it is too expensive you will remove the whole system instead.",
        "Leave it alone for two weeks before adjusting anything. The first three days of any block feel unreasonable; that feeling is not information.",
      ],
    },
    {
      type: "p",
      text: "If you are not sure which of those descriptions is yours — and most people are not, because the screen-time number does not distinguish between them — {the-phone-and-faith-test} is twelve questions that do. It separates four patterns, and the response that works for one of them is the response that fails for the others.",
      links: { "the-phone-and-faith-test": "/quiz/phone-and-faith-test" },
    },
  ],
  faq: [
    {
      q: "Can I stop myself from ignoring Screen Time limits?",
      a: "Not reliably on your own — “Ignore Limit” is one tap and there is nobody on the other end of the request. The workable versions either give the passcode to someone else, or replace the dismiss button with something you have to do rather than something you can wave away.",
    },
    {
      q: "Does deleting the app defeat the point?",
      a: "Any blocker on iPhone can be removed by deleting it, and no app can prevent that. What it can do is make removal a deliberate decision rather than an accident — a thing you have to mean, rather than a button you hit on the way past.",
    },
    {
      q: "Are app blockers on Android as strong?",
      a: "No. Apple's Screen Time framework lets an app hold a block that the user cannot casually undo; Android has no equivalent an ordinary app can rely on. That is why several blockers keep the strong version for iPhone. Bible Shield is iPhone-only altogether today — its Android version is still in development.",
    },
  ],
  related: [
    "how-to-stop-scrolling-at-night",
    "christian-app-blocker-guide",
    "screenzen-alternative",
  ],
};
